import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { filter, take } from 'rxjs/operators';
import { VoucherService } from '../../services/voucher.service';
import { PermissionService } from '../../services/permission.service';
import { UserPermission } from '../../models/permission.model';
import { VoucherDto, VoucherStatus, getVoucherStatusLabel } from '../../models/voucher.model';
import { VoucherFilterParams } from '../../models/voucher-filter.model';

@Component({
    selector: 'app-voucher-reports',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './voucher-reports.component.html',
    styleUrl: './voucher-reports.component.css',
    animations: [
        trigger('slideDown', [
            transition(':enter', [
                style({ height: 0, opacity: 0, overflow: 'hidden' }),
                animate('300ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ height: 0, opacity: 0, overflow: 'hidden' }))
            ])
        ])
    ]
})
export class VoucherReportsComponent implements OnInit {
    vouchers: VoucherDto[] = [];
    isLoading: boolean = false;
    errorMessage: string = '';

    // Filter parameters
    filters: VoucherFilterParams = {
        phoneNumber: undefined,
        customerName: undefined,
        partnerName: undefined,
        minVoucherAmount: undefined,
        maxVoucherAmount: undefined,
        partnerId: undefined,
        outletId: undefined,
        status: undefined,
        redemptionReference: undefined,
        fromDate: undefined,
        toDate: undefined,
        pageIndex: 0,
        pageSize: 20
    };

    // UI State
    showFilters: boolean = true;
    expandedVoucherId: string | null = null;

    // Pagination state
    totalCount: number = 0;
    totalPages: number = 0;
    currentPage: number = 1;

    // Expose enum to template
    VoucherStatus = VoucherStatus;

    // Expose Math for template
    Math = Math;

    constructor(
        private voucherService: VoucherService,
        private permissionService: PermissionService
    ) { }

    ngOnInit(): void {
        // Wait for permissions to be loaded before fetching vouchers
        this.permissionService.permissions$.pipe(
            filter(permissions => permissions.length > 0),
            take(1)
        ).subscribe(() => {
            this.loadVouchers();
        });
    }

    /**
     * Load vouchers with current filters
     */
    loadVouchers(): void {
        this.isLoading = true;
        this.errorMessage = '';

        // Determine which API to call based on user permissions
        const voucherObservable = this.permissionService.hasPermission(UserPermission.Partner_ViewVouchers)
            ? this.voucherService.getPartnerVouchers(this.filters)
            : this.voucherService.getOutletVouchers(this.filters);

        voucherObservable.subscribe({
            next: (response) => {
                this.vouchers = response.items;
                this.totalCount = response.totalCount;
                // API returns 0-based pageNumber, convert to 1-based for UI
                this.currentPage = response.pageNumber + 1;
                this.totalPages = Math.ceil(response.totalCount / response.pageSize);
                this.isLoading = false;
            },
            error: (error) => {
                this.isLoading = false;
                this.errorMessage = error.error?.message || 'Failed to load vouchers. Please try again.';
                console.error('Error loading vouchers:', error);
            }
        });
    }

    /**
     * Apply filters and reload vouchers
     */
    applyFilters(): void {
        this.filters.pageIndex = 0; // Reset to first page
        this.loadVouchers();
    }

    /**
     * Clear all filters
     */
    clearFilters(): void {
        this.filters = {
            phoneNumber: undefined,
            customerName: undefined,
            partnerName: undefined,
            minVoucherAmount: undefined,
            maxVoucherAmount: undefined,
            partnerId: undefined,
            outletId: undefined,
            status: undefined,
            redemptionReference: undefined,
            fromDate: undefined,
            toDate: undefined,
            pageIndex: 0,
            pageSize: 20
        };
        this.loadVouchers();
    }

    /**
     * Toggle filter panel
     */
    toggleFilters(): void {
        this.showFilters = !this.showFilters;
    }

    /**
     * Toggle voucher details expansion
     */
    toggleVoucherDetails(voucherId: string | undefined): void {
        if (!voucherId) return;
        if (this.expandedVoucherId === voucherId) {
            this.expandedVoucherId = null;
        } else {
            this.expandedVoucherId = voucherId;
        }
    }

    /**
     * Check if voucher is expanded
     */
    isExpanded(voucherId: string | undefined): boolean {
        return this.expandedVoucherId === voucherId;
    }

    /**
     * Get expanded voucher
     */
    getExpandedVoucher(): VoucherDto | null {
        if (!this.expandedVoucherId) return null;
        return this.vouchers.find(v => v.id === this.expandedVoucherId) || null;
    }

    /**
     * Get status label for display
     */
    getStatusLabel(status?: VoucherStatus): string {
        return getVoucherStatusLabel(status);
    }

    /**
     * Get status CSS class
     */
    getStatusClass(status?: VoucherStatus): string {
        switch (status) {
            case VoucherStatus.Active:
                return 'status-active';
            case VoucherStatus.Redeemed:
                return 'status-redeemed';
            case VoucherStatus.Expired:
                return 'status-expired';
            case VoucherStatus.Cancelled:
                return 'status-cancelled';
            case VoucherStatus.Revoked:
                return 'status-revoked';
            default:
                return '';
        }
    }

    /**
     * Format date for display
     */
    formatDate(dateString?: string): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    }

    /**
     * Format date and time for display
     */
    formatDateTime(dateString?: string): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Export vouchers to CSV
     */
    exportToCSV(): void {
        if (this.vouchers.length === 0) {
            alert('No vouchers to export');
            return;
        }

        const headers = ['Voucher Code', 'Customer Name', 'Phone', 'Voucher Amount', 'Status', 'Redemption Reference', 'Expiry Date', 'Redeemed At'];
        const rows = this.vouchers.map(v => [
            v.voucherCode,
            v.customerName,
            v.customerPhoneNumber,
            v.voucherAmount?.toString() || '0',
            this.getStatusLabel(v.status),
            v.redemptionReference || 'N/A',
            this.formatDate(v.expiryDate),
            v.redeemedAt ? this.formatDateTime(v.redeemedAt) : 'N/A'
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `vouchers_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    /**
     * Check if any filters are active
     */
    hasActiveFilters(): boolean {
        return !!(
            this.filters.phoneNumber ||
            this.filters.customerName ||
            this.filters.partnerName ||
            this.filters.minVoucherAmount ||
            this.filters.maxVoucherAmount ||
            this.filters.partnerId ||
            this.filters.outletId ||
            this.filters.redemptionReference ||
            this.filters.fromDate ||
            this.filters.toDate ||
            this.filters.status !== undefined
        );
    }

    /**
     * Go to a specific page
     */
    goToPage(page: number): void {
        if (page < 1 || page > this.totalPages) return;
        this.filters.pageIndex = page - 1; // API uses 0-based index
        this.loadVouchers();
    }

    /**
     * Go to next page
     */
    nextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.goToPage(this.currentPage + 1);
        }
    }

    /**
     * Go to previous page
     */
    previousPage(): void {
        if (this.currentPage > 1) {
            this.goToPage(this.currentPage - 1);
        }
    }

    /**
     * Get array of page numbers for pagination UI
     */
    getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

        // Adjust start page if we're near the end
        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    }

    /**
     * Handle page size changes
     */
    onPageSizeChange(size: number): void {
        this.filters.pageSize = size;
        this.filters.pageIndex = 0; // Reset to first page
        this.loadVouchers();
    }
}
